import React, {useContext, useEffect, useRef, useState} from 'react';
import WholesalerIcon from '../../../assests/img/wholesale-icon.svg?react';
import CloseIcon from '../../../assests/img/close.svg?react';
import EditIcon from '../../../assests/img/edit.svg?react';
import DeleteIcon from '../../../assests/img/delete.svg?react';
import NoLogoImg from '../../../assests/img/no-logo.jpg?url';
import InfoType from './InfoType';
import classNames from 'classnames';
import TextInput from './TextInput';
import Edit from './Edit';
import {updateField} from '../../Content';
import {getUserPermisson, wholesalerBg} from '../functions/utils';
import {filterData} from './DropdownFilter';
import {FilterContext} from '../context/filterContext';
import {isFilterSelected} from '../functions/filter';
import {UserContext} from '../context/userContext';
import {toast} from 'react-toastify';
import {uploadFile} from '../functions/handleFiles';
import Loader from './Loader';
import Popup from './Popup';

export interface cardHeaderProps{
    name: string,
    type: string,
    distribution: string,
    imgUrl: string,
    view: 'brief' | 'detail',
    edit: boolean,
    add: boolean,
    categories: filterData[],
    groupId: number,
    onClose: ()=> void,
    onWholesalerSelect: (selecetd: boolean)=> void,
    onGroupIdSelected: (id: number)=> void,
    onEdit?: ()=> void,
    onUpdate?: (reqBody: object, field: updateField) => Promise<void> | void;
    onDelete?: ()=> void;
}

export default function CardHeader(props: cardHeaderProps) {

    const {name, type, imgUrl, view, distribution, onClose,
        add, edit, onEdit, onUpdate, onGroupIdSelected,
        onWholesalerSelect, categories, onDelete, groupId} = props;
    
    const {filterData} = useContext(FilterContext);
    const {data: {permissions}} = useContext(UserContext);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [carrierName, setCarrierName] = useState<string>(name);
    const [showCategoryTooltip, setShowCategoryTooltip] = useState(false);
    const [showCarrierSelect, setShowCarrierSelect] = useState(false);
    const [carrierList, setCarrierList] = useState(filterData.carrier);
    const [loading, setLoading] = useState(false);
    const [unsavedPopup, setUnsavedPopup] = useState('');
    const [categoryLoading, setCategoryLoading] = useState(false);

    const showWholesalerIcon = (input: string) => input.toLowerCase().trim() === 'wholesale' &&
        type.toLowerCase().trim() !== 'wholesaler'? true: false;
    
    
    const handleCarrierSelect = (id: number, value: string) => {
        if (add && onUpdate) {
            onUpdate({id, value}, updateField.name);
            setCarrierName(value);
        }
        setShowCarrierSelect(false);
    }

    const handleCarrierNameUpdate = async (text?: string) => {
        if (add && onUpdate) {
            onUpdate({value: text}, updateField.name);
            setCarrierName(text? text: '');
            const temp = carrierList.filter(({value})=>
                value.toLowerCase().includes(text? text.toLowerCase(): ''));
            setCarrierList(temp);
            if (temp.length === 0) {
                setShowCarrierSelect(false);
            }
            if (text?.length === 0) {
                setCarrierList(filterData.carrier);
            } 
        } else {
            if (onUpdate) {
                if (carrierName.trim().length === 0) {
                    toast.error("Please enter name");
                } else {
                    await onUpdate({
                        value: carrierName,
                    }, updateField.name);
                    setCarrierName(carrierName);
                }
            }
        }
    }

    const handleDistributionUpdate = async (selected: string,
        field: updateField) => {
        const reqBody = field === updateField.distribution?
            {Distribution: selected}: {CompanyType: selected};
        if (onUpdate) {
            await onUpdate(reqBody, field);
        }
    }

    const handleCategoryUpdate = async (id: number) => {
        setCategoryLoading(true);
        let reqBody = {};
        const selected = categories.filter((data)=> data.id === id);
        if (selected.length) {
            reqBody = {
                categoryGroupId: selected[0].groupId,
            }
        } else {
            reqBody = {
                categoryId: id,
            }
        }
        if (onUpdate) {
            await onUpdate(reqBody, updateField.category);
        }
        setCategoryLoading(false);
    }
    
    const handleWholesalerSelected = (e: React.MouseEvent<HTMLSpanElement,
        MouseEvent>) => {
        e.stopPropagation();
        if (view === 'detail') {
            onGroupIdSelected(groupId);
        } else {
            onGroupIdSelected(0);
        }
        onWholesalerSelect(true);
    }

    const wholesalerIcon = () => {
        return showWholesalerIcon(distribution)?
            <span onClick={(e) => handleWholesalerSelected(e)}>
                <WholesalerIcon height={32} width={32}/>
            </span>: null;
    }

    const handleDelete = () => {
        if (onDelete) {
            onDelete()
        }
    }

    const handleClickOutside=(event: MouseEvent)=> {
        if (dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)) {
            setShowCarrierSelect(false);
        }
    }

    const handleUploadFile = async (files: FileList) => {
        Array.from(files).forEach(async (eachFile)=> {
            setLoading(true);
            const {fileUrl, res, error} =
                await toast.promise(uploadFile(eachFile, 'logo'),
                {pending: `Uploading logo - ${eachFile.name}`});
            if (!error && res?._response.status === 201) {
                if (onUpdate) {
                    const reqBody = {
                        Logo: fileUrl,
                    }
                    await onUpdate(reqBody, updateField.logo);
                    setLoading(false);
                }
            } else if (error) {
                setLoading(false);
                toast.error(error);
            }
        })
    }

    const handleUploadFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
        const tempFiles = event.target.files;
        if (tempFiles) {
            handleUploadFile(tempFiles);
        }
    }

    const handleOnClose = () => {
        let updated = ''
        if (name !== carrierName) {
            updated = 'Name is changed but not updated'
        } else {
            onClose();
        }
        setUnsavedPopup(updated);
    }

    useEffect(()=> {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    },[]);

    return (
        <>
        <Popup show={unsavedPopup.length? true: false}
            title='Are you sure you want to close?'
            onClose={()=> setUnsavedPopup('')}
            primaryCtaOnClick={()=> setUnsavedPopup('')}
            primaryCtaText='Continue to Update'
            secondaryCtaText='Close'
            secondaryCtaOnClick={props.onClose}>
            {unsavedPopup}
        </Popup>
        <div className={classNames('card-headers',
        wholesalerBg(type, distribution)?
           view === 'detail'? 'card-detail-wholesaler-bg':
            'card-wholesaler-bg': '',
            view === 'brief'? 'card-header-brief': '')}>
            <div className="card-carrier-logo-cotainer">
                {edit && !add?
                <input type="file"
                    className="card-logo-img-upload"
                    multiple={false}
                    accept='image/png, image/gif, image/jpeg'
                    onChange={handleUploadFiles}/>: null}
                {loading? <Loader/>:
                <img alt="carrier logo" className="card-carrier-logo"
                    src={imgUrl !== null ?imgUrl: NoLogoImg}
                    height={view === 'brief'? '56px': '80px'}
                    width={view === 'brief'? '56px': '80px'}>
                </img>}
            </div>
            <div className={classNames('card-carrier-details',
                view === 'detail'? 'detail-view': '')}
                onMouseLeave={!edit? ()=>
                    setShowCategoryTooltip(false): ()=> null}>
                <div className="card-detail-align detail">
                    {showCategoryTooltip?
                        <div className="card-category-icon-tooltip">
                            {categories.map((category)=> {
                                return (
                                    <div key={`category-tooltip-${category.id}`} className="card-category-tooltip-item">
                                        <span> <img src={category.logo}
                                            alt={category.value}
                                            width={'20px'}/>
                                        </span>
                                        <span>{category.value}</span>
                                    </div>
                                )
                            })}
                        </div>
                    : null}
                    {!edit? <div className="card-carrier-icons"
                        onMouseEnter={!edit? ()=>
                            setShowCategoryTooltip(true): ()=> null}>
                        {categories.map((category)=> {
                            return (
                                <span key={`category-icon-${category.id}`}
                                className="card-category-icon">
                                    <img src={category.logo}
                                        alt={category.value}
                                        width={'20px'}/>
                                </span>
                            )
                        })}
                    </div>:
                    <div className="card-carrier-icons"
                    onMouseEnter={!edit? ()=>
                        setShowCategoryTooltip(true): ()=> null}>
                    {!add && filterData.categories.map((category)=> {
                        return (
                            <span key={`category-list-icon-${category.id}`}
                            className={classNames('card-category-icon',
                                    isFilterSelected(category.id, categories)?
                                    'selected': 'not-selected')}
                                onClick={()=>
                                    handleCategoryUpdate(category.id)}>
                                <img src={category.logo} alt={category.value}
                                    width={'20px'} className='card-category-img'/>
                            </span>
                        )
                    })}
                    {categoryLoading? <span className="edit-loader"/>: null}
                    </div>}
                    {view === 'detail'?
                    <span className="card-detail-edit-icon">
                    {onEdit &&
                        getUserPermisson(permissions, 'U')?
                        <EditIcon onClick={onEdit}
                            fill={edit? 'rgb(0, 88, 221)': '#fff'}
                            width={16} height={16}/>: null}
                    {getUserPermisson(permissions, 'D') && !add?
                        <DeleteIcon width={16} height={16}
                            onClick={handleDelete}/>: null}
                    <span className="popup-close right-align"> 
                        <CloseIcon onClick={handleOnClose} width={24}/> </span>
                    </span>: null}
                </div>
                <div className="card-carrier-name" style={{
                    fontSize: view === 'brief'? '14px': '20px'
                    }} onClick={()=> setShowCarrierSelect(true)}>
                        {edit || add?
                            <TextInput type='text' text={carrierName}
                                placeholder='Enter Carrier Name'
                                className='carrier-text-edit'
                                onChange={add? (text)=>
                                    handleCarrierNameUpdate(text):
                                    (text)=> setCarrierName(text)}/>:
                            <span>{name}</span>}
                        {add?
                        showCarrierSelect?
                        <div ref={dropdownRef}
                            className="carrier-names-dropdown">
                            {carrierList.slice(1).map((eachCarrier, index)=> {
                                return (
                                    <div
                                    key={`card-header-carrier-select-${index}`} className="carrier-name-select"
                                    onClick={()=> handleCarrierSelect(
                                        eachCarrier.id, eachCarrier.value
                                    )}>
                                        <span>{eachCarrier.value}</span>
                                    </div>
                                )
                            })}
                        </div>: null: null}
                    {view === 'detail'?
                    edit && !add?
                    <Edit
                        onClose={()=> setCarrierName(name)}
                        onUpdate={handleCarrierNameUpdate}/>: null:
                        null}
                    
                </div>
                {view === 'detail'?<div className="card-into-type">
                    <InfoType type={distribution} showDropdown={props.edit}
                        field='distribution'
                        onUpdate={(selected)=>
                            handleDistributionUpdate(selected,
                                updateField.distribution)}/>
                    <InfoType type={type} showDropdown={props.edit}
                        field='type'
                        onUpdate={(selected)=>
                            handleDistributionUpdate(selected,
                                updateField.type)}/>
                    <span className="card-carrier-wholesale right-align">
                        {wholesalerIcon()}
                    </span>
                </div>: null}   
            </div>
            {view === 'brief'? <div className="card-carrier-wholesale right-align">
                {wholesalerIcon()}
            </div>: null}
        </div></>
    );

}